import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Users, CheckCircle, Quote } from 'lucide-react';
import { speakingData, images } from '../data/mock';

const SpeakingPage = () => {
  return (
    <main>
      {/* Hero Section with Background Image */}
      <section className="min-h-[85vh] flex items-center relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={images.speaking1}
            alt="Tyrre Burks speaking at an event"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/70 via-[#0d0d0d]/50 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent"></div>
        </div>
        
        <div className="container-custom relative z-10 pt-32 pb-20">
          <div className="max-w-2xl">
            <span className="caption text-[#c9a86c] mb-4 block opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>Speaking</span>
            <h1 className="heading-1 text-[#f5f5f0] mb-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
              {speakingData.headline}
            </h1>
            <div className="gold-accent mb-8 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}></div>
            <p className="body-large text-[#a8a8a0] opacity-0 animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
              {speakingData.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Speaking Reel Video */}
      <section className="section-padding bg-[#0d0d0d]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="caption text-[#c9a86c] mb-4 block">Watch Tyrre</span>
            <h2 className="heading-2 text-[#f5f5f0] mb-6">Speaking Reel</h2>
            <div className="gold-accent mx-auto"></div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/UpR3-ifY7-U"
                title="Tyrre Burks Speaking Reel"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* John Maxwell Testimonial */}
      <section className="section-padding bg-[#1a1a18]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="caption text-[#c9a86c] mb-4 block">Featured Testimonial</span>
            <h2 className="heading-2 text-[#f5f5f0] mb-6">John Maxwell on Tyrre's Mission</h2>
            <div className="gold-accent mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Video */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/z128tvf9Zs4"
                title="John Maxwell on Tyrre Burks"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>

            {/* Content */}
            <div>
              <div className="mb-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_1e5c107d-48e5-49a9-bc86-236f456dfd6e/artifacts/eztozxio_HCS-06.jpg"
                  alt="Tyrre Burks with John Maxwell at High Capacity Speakers"
                  className="w-full h-auto max-h-[300px] object-cover object-top"
                />
              </div>
              
              <blockquote className="border-l-2 border-[#c9a86c] pl-6 mb-6">
                <p className="text-xl font-display italic text-[#f5f5f0] mb-4">
                  "When we know why we're here and what matters, we don't need to know everything else. This clarity and conviction can take us exactly where God created us to go."
                </p>
                <footer className="text-[#c9a86c] font-medium">
                  — John Maxwell
                </footer>
              </blockquote>
              
              <p className="body-medium text-[#a8a8a0]">
                Leadership expert John Maxwell highlights the remarkable clarity of Tyrre's mission and the unwavering conviction that flows from it. He celebrates how Tyrre's passion is contagious—emphasizing the powerful distinction between being burned out and being on fire.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Speaking Topics */}
      <section className="section-padding bg-[#0d0d0d]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-[#c9a86c] mb-4 block">Keynote Topics</span>
            <h2 className="heading-2 text-[#f5f5f0] mb-6">What Tyrre Speaks About</h2>
            <div className="gold-accent mx-auto"></div>
          </div>

          <div className="space-y-8">
            {speakingData.topics.map((topic, index) => (
              <div
                key={topic.title}
                className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-8 md:p-12 card-hover"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                  <div className="lg:col-span-8">
                    <span className="caption text-[#c9a86c] mb-4 block">Topic {index + 1}</span>
                    <h3 className="heading-2 text-[#f5f5f0] mb-4">{topic.title}</h3>
                    <p className="body-large text-[#a8a8a0] mb-6">{topic.description}</p>
                  </div>
                  <div className="lg:col-span-4 space-y-4">
                    <div className="flex items-center gap-3 text-[#a8a8a0]">
                      <Clock size={18} className="text-[#c9a86c]" />
                      <span>{topic.duration}</span>
                    </div>
                    <div className="flex items-start gap-3 text-[#a8a8a0]">
                      <Users size={18} className="text-[#c9a86c] mt-1" />
                      <span>{topic.ideal}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="section-padding bg-[#0d0d0d]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="caption text-[#c9a86c] mb-4 block">What You Get</span>
              <h2 className="heading-2 text-[#f5f5f0] mb-6">Every Engagement Includes</h2>
              <div className="gold-accent mb-8"></div>
              
              <ul className="space-y-4">
                {[
                  'Pre-event consultation to understand your audience',
                  'Customized content aligned with your event theme',
                  'Practical frameworks your attendees can implement immediately',
                  'Q&A session for deeper engagement',
                  'Post-event resources for continued learning',
                  'Optional workshop add-on for hands-on application'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#c9a86c] mt-1 flex-shrink-0" />
                    <span className="body-medium text-[#a8a8a0]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-10">
              <h3 className="heading-3 text-[#f5f5f0] mb-6">Past Clients Include</h3>
              <div className="space-y-4">
                {speakingData.pastClients.map((client, index) => (
                  <div
                    key={index}
                    className="py-4 border-b border-[rgba(168,168,160,0.1)] last:border-0"
                  >
                    <span className="body-medium text-[#a8a8a0]">{client}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0d0d0d] relative">
        <div className="absolute inset-0 border-t border-[rgba(168,168,160,0.1)]"></div>
        <div className="container-custom relative z-10 text-center">
          <span className="caption text-[#c9a86c] mb-4 block">Ready to Book?</span>
          <h2 className="heading-2 text-[#f5f5f0] mb-6 max-w-2xl mx-auto">
            Let's Bring Clarity to Your Next Event
          </h2>
          <p className="body-large text-[#a8a8a0] mb-10 max-w-xl mx-auto">
            Whether it's a keynote, workshop, or full-day retreat, Tyrre delivers experiences that transform.
          </p>
          <Link to="/book" className="btn-primary inline-flex items-center gap-2">
            Request Availability
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default SpeakingPage;